import React, { useEffect } from "react";
import {
  Box, Typography, TextField, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Radio, RadioGroup,
  IconButton, useTheme,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PaymentsIcon from "@mui/icons-material/Payments";

import { currencyFormatter } from "utilities/currencyFormatter";
import { useFormContextCreateReceipt } from "../forms/formSetups/receiptCreateForm";
import { useFieldArray } from "react-hook-form";

const ReceiptConfirmView = ({
  total,
  paymentModes,
  paymentsTotal,
  handleChangeAmount,
  handleChangeMultiplePymentModes,
  multiplePayments,
  receiptDate,
  handleChangePaymentDate,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";
  const bodyBg = theme.palette.background.alt;

  const { register, watch, control, formState: { errors } } = useFormContextCreateReceipt();
  const { append, remove, fields, replace } = useFieldArray({ name: "payment_modes", control });

  /* ── Seed one row on mount ── */
  useEffect(() => {
    if (fields.length === 0) {
      append({ payment_type: "", amount_paid: 0 });
    }
  }, [append, fields.length]);

  /* ── Auto-select cash in single mode when payment types load ── */
  useEffect(() => {
    if (!multiplePayments && paymentModes.length > 0 && fields.length > 0) {
      const cashMode = paymentModes.find((pm) =>
        pm.payment_type_name?.toLowerCase().includes("cash")
      );
      if (cashMode && !fields[0]?.payment_type) {
        replace([{ payment_type: cashMode.id, amount_paid: 0 }]);
      }
    }
  }, [paymentModes, multiplePayments, fields, replace]);

  /* ── Watch fires on every form change — always recalculates ── */
  useEffect(() => {
    const subscription = watch((value) => {
      handleChangeAmount(value.payment_modes, multiplePayments);
    });
    return () => subscription.unsubscribe();
  }, [watch, multiplePayments, handleChangeAmount]);

  const fieldSx = {
    "& .MuiInputBase-root": { fontSize: "13px", backgroundColor: bodyBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "13px" },
    "& .MuiSelect-select": { fontSize: "13px" },
  };

  const balance = Math.round(parseFloat(paymentsTotal) - parseFloat(total));
  const balanceColor = balance === 0 ? accent : balance > 0 ? "#BA7517" : "#e24b4a";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* ── Amount banner + date ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: "1rem",
        alignItems: "center", justifyContent: "space-between",
        p: "1rem 1.25rem",
        backgroundColor: "rgba(29,158,117,0.06)",
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{ width: "36px", height: "36px", borderRadius: "9px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <PaymentsIcon sx={{ fontSize: "20px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
              Amount to be paid
            </Typography>
            <Typography sx={{ fontSize: "20px", fontWeight: 500, color: accent }}>
              {currencyFormatter(parseFloat(total))}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
            Date of payment
          </Typography>
          <TextField
            size="small"
            type="datetime-local"
            value={receiptDate}
            onChange={handleChangePaymentDate}
            sx={{ ...fieldSx, width: "210px" }}
          />
        </Box>
      </Box>

      {/* ── Payment mode toggle ── */}
      <Box>
        <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec, mb: "0.5rem" }}>
          Payment mode
        </Typography>
        <RadioGroup
          row
          value={multiplePayments}
          onChange={() => {
            handleChangeMultiplePymentModes();
            replace([{ payment_type: "", amount_paid: 0 }]);
          }}
          sx={{ gap: "0.5rem" }}
        >
          {[
            { value: false, label: "Single payment mode" },
            { value: true, label: "Multiple payment modes" },
          ].map(({ value, label }) => (
            <Box
              key={String(value)}
              sx={{
                display: "flex", alignItems: "center",
                px: "0.75rem", py: "0.35rem",
                borderRadius: "20px",
                border: `1px solid ${String(multiplePayments) === String(value) ? accent : border}`,
                backgroundColor: String(multiplePayments) === String(value) ? "rgba(29,158,117,0.1)" : "transparent",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <FormControlLabel
                value={value}
                control={<Radio size="small" sx={{ p: "3px", color: textSec, "&.Mui-checked": { color: accent } }} />}
                label={<Typography sx={{ fontSize: "13px", color: String(multiplePayments) === String(value) ? accent : textSec }}>{label}</Typography>}
                sx={{ m: 0 }}
              />
            </Box>
          ))}
        </RadioGroup>
      </Box>

      {/* ── Payment entry rows ── */}
      <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden" }}>
        <Box sx={{ px: "1.25rem", py: "0.75rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
            {multiplePayments ? "Payment modes" : "Payment"}
          </Typography>
        </Box>

        <Box sx={{ p: "0.75rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {fields.map((payment_mode, index) => {
            const modeError = errors?.payment_modes?.[index]?.payment_type?.message;
            const amountError = errors?.payment_modes?.[index]?.amount_paid?.message;

            return (
              <Box
                key={payment_mode.id}
                sx={{
                  display: "flex", gap: "0.75rem", alignItems: "flex-start",
                  p: "0.875rem",
                  backgroundColor: isDark ? "rgba(255,255,255,0.015)" : "#f9faf9",
                  border: `1px solid ${modeError || amountError ? "rgba(226,75,74,0.4)" : border}`,
                  borderRadius: "8px",
                  transition: "border-color 0.15s",
                }}
              >
                {/* Mode select */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                  <FormControl size="small" error={!!modeError} sx={{ ...fieldSx, width: "100%" }}>
                    <InputLabel>Payment mode</InputLabel>
                    <Select
                      label="Payment mode"
                      defaultValue={
                        !multiplePayments && index === 0
                          ? (paymentModes.find((pm) => pm.payment_type_name?.toLowerCase().includes("cash"))?.id ?? "")
                          : ""
                      }
                      {...register(`payment_modes.${index}.payment_type`)}
                    >
                      {paymentModes.map((pm, i) => (
                        <MenuItem key={i} value={pm?.id}>{pm.payment_type_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {modeError && (
                    <Typography sx={{ fontSize: "11px", color: "#e24b4a", px: "4px" }}>{modeError}</Typography>
                  )}
                </Box>

                {/* Amount */}
                <Box sx={{ flex: 1.5, display: "flex", flexDirection: "column", gap: "4px" }}>
                  <TextField
                    label="Amount received"
                    type="number"
                    size="small"
                    defaultValue={0}
                    autoFocus={index === 0}
                    error={!!amountError}
                    {...register(`payment_modes.${index}.amount_paid`)}
                    sx={{ ...fieldSx, width: "100%" }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  {amountError && (
                    <Typography sx={{ fontSize: "11px", color: "#e24b4a", px: "4px" }}>{amountError}</Typography>
                  )}
                </Box>

                {/* Remove — multiple mode only */}
                {multiplePayments && (
                  <IconButton
                    onClick={() => remove(index)}
                    size="small"
                    sx={{
                      mt: "2px", color: "#e24b4a",
                      borderRadius: "7px",
                      border: "1px solid rgba(226,75,74,0.25)",
                      "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" },
                      flexShrink: 0,
                    }}
                  >
                    <CancelOutlinedIcon sx={{ fontSize: "17px" }} />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {/* Add payment mode — multiple mode only */}
          {multiplePayments && (
            <Box
              onClick={() => append({ payment_type: "", amount_paid: 0 })}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                p: "0.65rem", borderRadius: "8px",
                border: `1px dashed ${isDark ? "rgba(29,158,117,0.3)" : "rgba(29,158,117,0.4)"}`,
                cursor: "pointer", color: accent,
                fontSize: "13px", fontWeight: 500,
                transition: "all 0.15s",
                "&:hover": { backgroundColor: "rgba(29,158,117,0.05)" },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: "16px" }} /> Add payment mode
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Totals ── */}
      <Box sx={{ backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden" }}>
        {[
          { label: "Amount tendered", value: currencyFormatter(parseFloat(paymentsTotal || 0)), color: textPri, bold: false },
          { label: "Amount to be paid", value: currencyFormatter(parseFloat(total || 0)), color: textPri, bold: false },
          { label: "Balance", value: currencyFormatter(balance || 0), color: balanceColor, bold: true },
        ].map(({ label, value, color, bold }, i, arr) => (
          <Box key={label} sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            px: "1.25rem", py: "0.7rem",
            borderBottom: i < arr.length - 1 ? `1px solid ${border}` : "none",
            backgroundColor: bold
              ? (isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)")
              : "transparent",
          }}>
            <Typography sx={{ fontSize: "12px", color: textSec, fontWeight: bold ? 500 : 400 }}>{label}</Typography>
            <Typography sx={{ fontSize: bold ? "16px" : "13px", fontWeight: bold ? 600 : 500, color }}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ReceiptConfirmView;